export const base64EncodeFile = (file: File | string): Promise<string> => {
  // Si file est une chaîne (URL), récupérer l'image et la convertir en base64
  if (typeof file === 'string') {
    return new Promise((resolve, reject) => {
      // Vérifier si l'URL est valide
      try {
        new URL(file);
      } catch (e) {
        reject(new Error("URL d'image invalide"));
        return;
      }

      fetch(file)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Vérifier si le blob est une image ou si le type est vide (certains serveurs ne définissent pas correctement le type)
          if (
            !blob.type ||
            (!blob.type.startsWith('image/') &&
              blob.type !== 'application/octet-stream')
          ) {
            // Si le type est vide ou non reconnu mais que l'URL semble être une image, on continue quand même
            const imageExtensions = [
              '.jpg',
              '.jpeg',
              '.png',
              '.gif',
              '.webp',
              '.svg',
              '.bmp',
              '.ico',
            ];
            const isLikelyImage =
              imageExtensions.some(ext => file.toLowerCase().endsWith(ext)) ||
              file.includes('/avatar') ||
              file.includes('/image') ||
              file.includes('/photo');

            if (!isLikelyImage) {
              throw new Error("Le fichier récupéré n'est pas une image");
            }
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            if (!result) {
              reject(new Error('Échec de la lecture du fichier'));
              return;
            }
            const base64String = result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = error => {
            reject(error);
          };
          reader.readAsDataURL(blob);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  // Si file est un objet File, utiliser l'implémentation existante
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      // Vérifier l'extension du nom de fichier comme fallback
      const fileName = file.name.toLowerCase();
      const isImageByExtension = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.webp',
        '.svg',
        '.bmp',
        '.ico',
      ].some(ext => fileName.endsWith(ext));

      if (!isImageByExtension) {
        reject(new Error("Le fichier n'est pas une image"));
        return;
      }
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      if (!result) {
        reject(new Error('Échec de la lecture du fichier'));
        return;
      }
      const base64String = result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

export const encodeFiles = async (files: File[]) => {
  const encodedFiles = [];
  for (const file of files) {
    try {
      const mimeType = file.type;
      const base64String = await base64EncodeFile(file);
      encodedFiles.push({
        mimeType,
        base64String,
      });
    } catch (error) {
      // Do nothing
    }
  }
  return encodedFiles;
};
